import { BaseFirestoreDocument, Scope } from '@esg/esg-global-types';

const SCOPE_COLLECTION = 'scope_master_list';

/**
 * Ensures a Scope object carries the Firestore document reference path the v1 API needs
 * to persist scope. ScopeSelect fetches via the v2 API which omits `reference`; this
 * reconstructs it from the known master-list collection path.
 * TODO: Remove after migrating metric functions to V2 API: https://energy-partners.atlassian.net/jira/software/c/projects/GE/boards/10?selectedIssue=GE-2564
 *
 * Overloaded rather than given one widened signature: the four existing call sites
 * (`createPrimaryMetricRow`, `createCalculatedMetricRow`, `transformMasterListMetricInputToRow`,
 * `transformConfiguredMetricInputToRow`) assign the result into fields typed `Scope | null`, which
 * does not admit `number` — widening the return type for all callers would break them. Only a
 * caller that opts into the second overload (a legacy scope that may be numeric, per GE-2907
 * Finding 26) sees the widened `Scope | BaseFirestoreDocument | number | null` result.
 */
export function withScopeReference(scope: Scope | undefined | null): Scope | null;
export function withScopeReference(
  scope: Scope | BaseFirestoreDocument | number | undefined | null
): Scope | BaseFirestoreDocument | number | null;
export function withScopeReference(scope: unknown): unknown {
  // Legacy numeric scopes must pass through untouched, checked before the falsy-guard below:
  // `!0` is `true`, so a legitimate scope value of 0 would otherwise be misrouted into the
  // "no scope" branch and returned as `null` instead of unchanged. Spreading a number also yields
  // `{}`, which would produce `reference.path` `'scope_master_list/undefined'` and defeat the
  // server's numeric escape hatch (syntiro_api metric.ts:617, :789). See GE-2907.
  if (typeof scope === 'number') return scope;
  if (!scope) return null;
  if ((scope as Scope & { reference?: unknown }).reference) return scope;
  // A scope with no `id` would otherwise build `reference.path` as `'scope_master_list/undefined'`
  // — a dangling reference that satisfies the server's `!scopeReference?.path` guard (it has a
  // path, just a broken one) and gets written silently, defeating the whole point of this
  // function. Fail loudly instead.
  if (!(scope as Scope).id) {
    throw new Error('withScopeReference: cannot reconstruct scope reference, scope has no id.');
  }
  return { ...scope, reference: { path: `${SCOPE_COLLECTION}/${(scope as Scope).id}` } };
}

// Sort the returned scopes. Copied from function in the v1 api.
export const sortScopes = (scopes: Array<Scope>) => {
  return scopes.sort((scope_a: Scope, scope_b: Scope) => {
    const splits_a = scope_a.scope.split('.').map(Number);
    const splits_b = scope_b.scope.split('.').map(Number);
    const maxLen = Math.max(splits_a.length, splits_b.length);

    for (let i = 0; i < maxLen; i++) {
      const diff = (splits_a[i] || 0) - (splits_b[i] || 0);
      if (diff) return diff;
    }

    return 0;
  });
};
